import React from 'react';

import {assert} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  WorksheetAnalyzeButtonProcedureMethodQuery,
  WorksheetAnalyzeButtonProcedureMethodQueryVariables,
} from 'procedures/shared-components/analysis/__generated/WorksheetAnalyzeButtonProcedureMethodQuery';
import {
  WorksheetAnalyzeButtonQuery,
  WorksheetAnalyzeButtonQueryVariables,
} from 'procedures/shared-components/analysis/__generated/WorksheetAnalyzeButtonQuery';
import AnalyzeAssayModal from 'procedures/shared-components/analysis/AnalyzeAssayModal';
import {
  WORKSHEET_ANALYZE_BUTTON_PROCEDURE_METHOD_QUERY,
  WORKSHEET_ANALYZE_BUTTON_QUERY,
} from 'procedures/shared-components/analysis/WorksheetAnalyzeButtonQuery';
import {useProcedureWorksheetContext} from 'procedures/worksheets/ProcedureWorksheetContext';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import * as ModalContainer from 'ui-library/ModalContainer';

interface WorksheetAnalyzeButtonProps {
  className?: string;
  worksheetId: string;
  worksheetName: string;
  isWorksheetInReview: boolean;
  refetch: () => Promise<void>;
}

export const ANALYZE_BUTTON_TOOLTIP_TEXT = 'Analyze data from this worksheet';
export const LOADING_TOOLTIP_TEXT = 'Loading...';
export const DISABLED_BUTTON_TOOLTIP_TEXT = 'Analyzing data is disabled because the worksheet is in review.';
const ANALYZE_ASSAY_MODAL_KEY = 'Analyze assay';

export default function WorksheetAnalyzeButton(props: WorksheetAnalyzeButtonProps): JSX.Element {
  const {PMEIs} = useProcedureWorksheetContext();
  const accessiblePMEIFromContext = PMEIs.length > 0 && !isInaccessible(PMEIs[0]) ? PMEIs[0] : null;
  // The PMEIs we get from context only have the method task info, so we need to fetch additional fields
  const procedureMethodQueryResult = useSkippableBenchlingQuery<
    WorksheetAnalyzeButtonProcedureMethodQuery,
    WorksheetAnalyzeButtonProcedureMethodQueryVariables
  >(WORKSHEET_ANALYZE_BUTTON_PROCEDURE_METHOD_QUERY, {
    // We just fetch the first PMEI, since we're only fetching the procedure run and the analysis template
    // configurations, which are expected to be the same for all PMEIs being executed in the worksheet
    variables: {
      methodExecutionInstanceId: accessiblePMEIFromContext?.id ?? '',
    },
    skip: accessiblePMEIFromContext == null,
  });
  const pmei =
    procedureMethodQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(procedureMethodQueryResult.data.procedureMethodExecutionInstance)
      ? procedureMethodQueryResult.data.procedureMethodExecutionInstance
      : null;

  const onClick = async (): Promise<void> => {
    const {worksheet} = await doQuerySafe<WorksheetAnalyzeButtonQuery, WorksheetAnalyzeButtonQueryVariables>(
      {query: WORKSHEET_ANALYZE_BUTTON_QUERY},
      {variables: {id: props.worksheetId}}
    );

    if (isInaccessible(worksheet)) {
      assert(false, 'Expected worksheet to be accessible');
    } else {
      ModalContainer.open(
        ANALYZE_ASSAY_MODAL_KEY,
        <AnalyzeAssayModal
          onRequestClose={() => {
            ModalContainer.close(ANALYZE_ASSAY_MODAL_KEY);
          }}
          onRequestSubmit={props.refetch}
          worksheet={worksheet}
          pmei={pmei}
        />
      );
    }
  };

  return (
    <Tooltip
      placement={FloatingPlacement.TOP}
      text={
        props.isWorksheetInReview
          ? DISABLED_BUTTON_TOOLTIP_TEXT
          : pmei == null
            ? LOADING_TOOLTIP_TEXT
            : ANALYZE_BUTTON_TOOLTIP_TEXT
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={props.className}
        icon="analytics_analysis"
        onClick={onClick}
        text="Analyze"
        size={ButtonSize.MEDIUM}
        dataTestAttrs={{component: 'WorksheetAnalyzeButton', element: 'root'}}
        disabled={props.isWorksheetInReview || pmei == null}
      />
    </Tooltip>
  );
}
